export interface ParsedDetailedMessage {
  summary: string;
  problems: string[];
}

/**
 * Backend validation details arrive as a single string: a summary line followed by
 * one line per problem, e.g.
 *
 *   Failed to read OAS3 definition.
 *   attribute paths.'/pet'(put).requestBody.content.'application/xml'.schema is missing
 */
export const parseDetailedMessage = (message: string): ParsedDetailedMessage => {
  const [summary = '', ...problems] = message
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  return { summary, problems }
}

// Characters that only ever show up in OAS paths, JSON pointers or regular expressions
const CODE_CHARACTERS = /[/#~^\\$`({[]/
// A dot between two word characters (`components.schemas`) or a leading dot (`.servers.url`)
const DOTTED_PATH = /\w\.\w|^\./
// Keys such as `composable-homepage:get` or `status:up`
const COLON_KEY = /\w:\w/

const TRAILING_PUNCTUATION = ['.', ',', ';']
const BACKTICK = '`'

/**
 * Trimming is done by scanning rather than with an anchored `+$` regex, which backtracks
 * super-linearly on long runs of the trimmed character.
 */
const trim = (token: string, shouldTrim: (character: string) => boolean, trimStart = false): string => {
  let start = 0
  let end = token.length

  while (end > start && shouldTrim(token[end - 1])) {
    end--
  }
  while (trimStart && start < end && shouldTrim(token[start])) {
    start++
  }

  return token.slice(start, end)
}

const stripTrailingPunctuation = (token: string): string => trim(token, character => TRAILING_PUNCTUATION.includes(character))

export const stripBackticks = (token: string): string => trim(token, character => character === BACKTICK, true)

/**
 * Distinguishes the technical parts of a problem line (paths, refs, regular expressions)
 * from the plain English around them, so only the former get code styling.
 */
export const isCodeToken = (token: string): boolean => {
  const core = stripTrailingPunctuation(token)

  if (!core) {
    return false
  }

  return CODE_CHARACTERS.test(core) || DOTTED_PATH.test(core) || COLON_KEY.test(core)
}

export const splitIntoTokens = (problem: string): string[] => problem.split(/(\s+)/).filter(Boolean)

export interface KeyedItem {
  key: string;
  value: string;
}

export interface KeyedToken extends KeyedItem {
  isCode: boolean;
}

/** Positional keys, safe because the parsed lines are static and never reordered */
const withKeys = (values: string[]): KeyedItem[] =>
  values.map((value, position) => ({ key: `${position}:${value}`, value }))

export const toKeyedProblems = (problems: string[]): KeyedItem[] => withKeys(problems)

export const toKeyedTokens = (problem: string): KeyedToken[] =>
  withKeys(splitIntoTokens(problem)).map(({ key, value }) => ({
    key,
    value: isCodeToken(value) ? stripBackticks(value) : value,
    isCode: isCodeToken(value),
  }))
